import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner, Badge, Modal, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import DataTable from "react-data-table-component";
import { Btn, H3 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { useNavigate } from "react-router-dom";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import { Edit, Plus, Trash2, Eye, Truck, User, MapPin, DollarSign, FileText, AlertTriangle } from "react-feather";
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

const ViewAccident = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const { config } = useConfig();

    // Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedAccident, setSelectedAccident] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');

    const prevSearch = useRef(searchTerm);

    const fetchData = async (page = 1, size = 10, search = "", sortCol = "", sortDir = "") => {
        setLoading(true);
        try {
            const payload: any = {
                page,
                per_page: size,
                search,
            };
            if (sortCol) {
                payload.sort_by = sortCol;
                payload.sort_direction = sortDir;
            }
            const response = await apiPost("accidents/getall", payload);
            setData(response.data);
            setTotalRows(response.total);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching accidents:", error);
            toast.error("Failed to fetch accidents");
            setLoading(false);
        }
    };

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);

        return () => clearTimeout(timer);
    }, [currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };
    const handleSort = (column: any, sortDir: string) => {
        setSortColumn(column.sortField || "");
        setSortDirection(sortDir);
    };

    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_accident`, { state: { id } });

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await apiPost("accidents/delete", { id });
                toast.success("Record deleted successfully");
                fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
            } catch (error) {
                console.error("Error deleting record:", error);
                toast.error("Failed to delete record");
            }
        }
    };

    // Modal Helpers
    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }
    const handleView = (row: any) => {
        setSelectedAccident(row);
        setActiveTab('1');
        setViewModal(true);
    };

    const columns = [
        {
            name: <div>Sl. No</div>,
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: <div>Vehicle</div>,
            selector: (row: any) => row.vehicle?.registration_number || "N/A",
            sortable: true,
            sortField: "vehicle_id",
            wrap: true,
            cell: (row: any) => (
                <span className="text-primary fw-bold">
                    {row.vehicle?.registration_number || 'N/A'}
                    {row.vehicle?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>Driver</div>,
            selector: (row: any) => row.driver?.name || "N/A",
            sortable: true,
            sortField: "driver_id",
            wrap: true,
            cell: (row: any) => (
                <span>
                    {row.driver?.name || 'N/A'}
                    {row.driver?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>Incident Date</div>,
            selector: (row: any) => row.incident_date_time,
            cell: (row: any) => row.incident_date_time ? new Date(row.incident_date_time).toLocaleDateString('en-GB') : '',
            sortable: true,
            sortField: "incident_date_time",
            wrap: true
        },
        { name: <div>Severity</div>, selector: (row: any) => row.incident_severity, sortable: true, sortField: "incident_severity", wrap: true },
        // { name: <div>Location</div>, selector: (row: any) => row.incident_location, sortable: true, sortField: "incident_location", wrap: true },
        { name: <div>Total Amount ({config.currency})</div>, selector: (row: any) => row.actual_repair_cost ? Number(row.actual_repair_cost).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00', sortable: true, sortField: "actual_repair_cost", wrap: true },
        {
            name: <div>Status</div>,
            selector: (row: any) => row.accident_status,
            sortable: true,
            sortField: "accident_status",
            wrap: true,
            cell: (row: any) => (
                <Badge color={
                    row.accident_status === 'Closed' ? 'success' :
                        row.accident_status === 'In Progress' ? 'warning' :
                            row.accident_status === 'Open' ? 'danger' : 'secondary'
                } pill>
                    {row.accident_status}
                </Badge>
            )
        },
        {
            name: "Action",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('accident.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('accident.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('accident.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "160px"
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Accidents" parent="Safety" title="Accidents List" />
            <Container fluid>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>

                                <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                    <H3 className="text-primary">Accident Overview</H3>
                                    <Btn color="danger" onClick={() => navigate(`${process.env.PUBLIC_URL}/add_accident`)}>
                                        <Plus size={16} /> Add Accident
                                    </Btn>
                                </Col>

                                <>
                                    <Col xs={12} className="mb-3 d-flex justify-content-end">
                                        <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                    </Col>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                        sortServer
                                        onSort={handleSort}
                                        highlightOnHover
                                        striped
                                        responsive
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        customStyles={customStyles}
                                    />
                                </>

                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* View Accident Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                {selectedAccident && (
                    <div style={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', // Red gradient for accidents
                        padding: '1.5rem',
                        position: 'relative', width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={handleClose}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        ></button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <AlertTriangle size={30} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedAccident.vehicle?.registration_number}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedAccident.vehicle?.brand} {selectedAccident.vehicle?.model}</span>
                                    <span>•</span>
                                    <span>{new Date(selectedAccident.incident_date_time).toLocaleString('en-GB')}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    padding: '0.5rem 1rem',
                                    margin: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    {selectedAccident.accident_status}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Truck size={16} /> Incident Details
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <DollarSign size={16} /> Financial & Insurance
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '3' ? 'active' : ''}
                                onClick={() => toggleTab('3')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <FileText size={16} /> Documents
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedAccident && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Vehicle</label>
                                                    <div className="fw-semibold">
                                                        {selectedAccident.vehicle?.registration_number} ({selectedAccident.vehicle?.brand} {selectedAccident.vehicle?.model})
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Driver</label>
                                                    <div className="fw-semibold text-primary">{selectedAccident.driver?.name || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Severity</label>
                                                    <div className={`fw-bold fs-6 ${selectedAccident.incident_severity === 'Total Loss' ? 'text-danger' : selectedAccident.incident_severity === 'Major' ? 'text-warning' : 'text-info'}`}>
                                                        {selectedAccident.incident_severity}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Incident Time</label>
                                                    <div className="fw-semibold">{selectedAccident.incident_date_time ? new Date(selectedAccident.incident_date_time).toLocaleString('en-GB') : 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Location</label>
                                                    <div className="fw-semibold"><MapPin size={14} className="me-1" />{selectedAccident.incident_location || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <label className="text-muted small fw-bold text-uppercase">Description</label>
                                                <div className="p-3 bg-light rounded text-dark" style={{ minHeight: '80px' }}>
                                                    {selectedAccident.incident_description || <em className="text-muted">No description details.</em>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm mb-4">
                                        <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">Repair Costs</h6>
                                        <div className="row g-4">
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Estimated Cost ({config.currency})</label>
                                                <div className="fw-bold text-secondary">{selectedAccident.estimated_repair_cost ? ` ${Number(selectedAccident.estimated_repair_cost).toLocaleString()}` : 'N/A'}</div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Actual Repair Cost ({config.currency})</label>
                                                <div className="fw-bold fs-5 text-danger">{selectedAccident.actual_repair_cost ? ` ${Number(selectedAccident.actual_repair_cost).toLocaleString()}` : 'N/A'}</div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Approved Claim Amt ({config.currency})</label>
                                                <div className="fw-bold fs-5 text-success">{selectedAccident.approved_claim_amount ? ` ${Number(selectedAccident.approved_claim_amount).toLocaleString()}` : 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">Insurance Details</h6>
                                        <div className="row g-4">
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Provider</label>
                                                <div className="fw-semibold">{selectedAccident.insurance_provider || 'N/A'}</div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Policy Number</label>
                                                <div className="fw-semibold">{selectedAccident.insurance_policy_number || 'N/A'}</div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="text-muted small fw-bold text-uppercase">Claim Filed Date</label>
                                                <div className="fw-semibold">{selectedAccident.claim_filed_date ? new Date(selectedAccident.claim_filed_date).toLocaleDateString('en-GB') : 'N/A'}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Claim Status</label>
                                                <Badge color={selectedAccident.claim_status === 'Approved' ? 'success' : selectedAccident.claim_status === 'Rejected' ? 'danger' : 'info'}>
                                                    {selectedAccident.claim_status}
                                                </Badge>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Claim Number</label>
                                                <div className="fw-semibold">{selectedAccident.claim_number || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            {/* Police Report */}
                                            <div className="col-md-6">
                                                <div className="p-3 border rounded d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="fw-bold">Police Report</div>
                                                        <small className="text-muted">{selectedAccident.police_report ? 'Available' : 'Not Uploaded'}</small>
                                                    </div>
                                                    {selectedAccident.police_report && (
                                                        <a href={`${mediaUrl}${selectedAccident.police_report}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">View</a>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Repair Invoice */}
                                            <div className="col-md-6">
                                                <div className="p-3 border rounded d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="fw-bold">Repair Invoice</div>
                                                        <small className="text-muted">{selectedAccident.repair_invoice ? 'Available' : 'Not Uploaded'}</small>
                                                    </div>
                                                    {selectedAccident.repair_invoice && (
                                                        <a href={`${mediaUrl}${selectedAccident.repair_invoice}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">View</a>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Payment Receipt */}
                                            <div className="col-md-6">
                                                <div className="p-3 border rounded d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="fw-bold">Payment Receipt</div>
                                                        <small className="text-muted">{selectedAccident.payment_receipt ? 'Available' : 'Not Uploaded'}</small>
                                                    </div>
                                                    {selectedAccident.payment_receipt && (
                                                        <a href={`${mediaUrl}${selectedAccident.payment_receipt}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">View</a>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Insurance Claim Report */}
                                            <div className="col-md-6">
                                                <div className="p-3 border rounded d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="fw-bold">Insurance Claim Report</div>
                                                        <small className="text-muted">{selectedAccident.insurance_claim_report ? 'Available' : 'Not Uploaded'}</small>
                                                    </div>
                                                    {selectedAccident.insurance_claim_report && (
                                                        <a href={`${mediaUrl}${selectedAccident.insurance_claim_report}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">View</a>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Accident Photos */}
                                            <div className="col-12">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2">Accident Photos</h6>
                                                {selectedAccident.accident_photos ? (
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {(() => {
                                                            try {
                                                                let photos: string[] = [];

                                                                // Try to parse as JSON first
                                                                if (typeof selectedAccident.accident_photos === 'string') {
                                                                    try {
                                                                        photos = JSON.parse(selectedAccident.accident_photos);
                                                                    } catch {
                                                                        // If JSON parse fails, treat as single photo path
                                                                        photos = [selectedAccident.accident_photos];
                                                                    }
                                                                } else if (Array.isArray(selectedAccident.accident_photos)) {
                                                                    photos = selectedAccident.accident_photos;
                                                                }

                                                                if (Array.isArray(photos) && photos.length > 0) {
                                                                    return photos.map((photo: string, index: number) => (
                                                                        <a key={index} href={`${mediaUrl}${photo}`} target="_blank" rel="noopener noreferrer" className="d-block border rounded p-1 shadow-sm">
                                                                            <img src={`${mediaUrl}${photo}`} alt={`Accident ${index + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                                                        </a>
                                                                    ));
                                                                } else {
                                                                    return <div className="text-muted">No photos available.</div>;
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing accident photos:', e);
                                                                return <div className="text-danger">Error loading photos. Data: {String(selectedAccident.accident_photos).substring(0, 100)}</div>;
                                                            }
                                                        })()}
                                                    </div>
                                                ) : (
                                                    <div className="text-muted">No photos uploaded.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
};

export default ViewAccident;
